import { Config } from 'config';

import { forEach, map, invokeMap, every, get, uniq } from 'lodash';

import { Base, Deferred, resolve, whenAll, map$, invokeMap$ } from 'gear';

import { Matrix } from '../../geometry';

import { Figure } from '../figure';
import { Figures } from '../figures';

function error(m) {
  console.log(m);
}

function isDrawingRequest(type) {
  return type === 'render' || type === 'commit' || type === 'preview';
}

const cloneProcessor = function(Type,p,otherOwner,partOfGroup,ungrouping) {
  const np = new Type(otherOwner);

  const cache = p.figure._cache.clone(otherOwner);

  if( ungrouping ) {

    if( partOfGroup ) {
      cache.clear('globalPreMatrix');
      cache.clear('globalPreVectorBounds');
      cache.clear('globalPreBounds');
      cache.clear('globalMatrix');
      cache.clear('globalVectorBounds');
      cache.clear('globalBounds');
      cache.clear('globalMaterialBounds');
      if( ! otherOwner.isGroup ) {
        np._invalidateGrouping();
      }
    }
    else {
      cache.set('globalPreMatrix',new Matrix());
      cache.copy('preVectorBounds','globalPreVectorBounds');
      cache.copy('preBounds'      ,'globalPreBounds'      );

      cache.copy('matrix'        ,'globalMatrix'        );
      cache.copy('vectorBounds'  ,'globalVectorBounds'  );
      cache.copy('bounds'        ,'globalBounds'        );
      cache.copy('materialBounds','globalMaterialBounds');

      const parent = p.figure._owner;
      if( get(parent._owner,'isGroup') ) {
        np._invalidateGrouping();
      }
    }
  }

  otherOwner._cache = cache;
  return np;
};

export function updateArray$( things, type ) {
  const promises = [];
  for(let k = 0; k < things.length; ++k) {
    promises[k] = things[k].update$(type);
  }
  return whenAll(promises);
}

export function update$( things, type ) {
  if( 'update$' in things ) {
    return things.update$(type);
  }
  return updateArray$(things,type);
}

export function cache$(things) {
  return update$(things,'cache');
}

function renderArray$( things, type ) {
  return updateArray$(things,type) .then( () => {

  forEach( uniq( map( things, thing => thing.document ) ), doc => {
    doc.fireRenderEvents(type);
  });

  return things;
  });
}

export function render$(things) {
  if( 'render$' in things ) {
    return things.render$();
  }
  return renderArray$(things,'render');
}

export function preview$(things) {
  if( 'preview$' in things ) {
    return things.preview$();
  }
  return renderArray$(things,'preview');
}

export function commitArray$(things) {
  const waitFor = [];
  return map$( uniq( map( things, thing => {
    const doc = thing.document;
    if( ! doc ) {
      waitFor.push( thing.commit$() );
    }
    return doc;
  }) ), doc => doc.commit$ ) .then( () => {
    return whenAll(waitFor) .then( () => things );
  });
}

export function commit$(things) {
  if( 'commit$' in things ) {
    return things.commit$();
  }
  return commitArray$(things);
}

export function revertArray$(things) {
  return map$( uniq( map( things, thing => thing.document ) ), doc => doc.revert$ )
         .then( () => things );
}

export function revert$(things) {
  if( 'revert$' in things ) {
    return things.revert$();
  }
  return revertArray$(things);
}


function renderMethod(type) {
  return function() {
    return this.update$(type) .then( () => {
    const doc = this.document;
    if( doc ) {
      doc.fireRenderEvents(type);
    }
    });
  };
}

export function updateMethods() {
  return {

    cache$() {
      return this.update$('cache');
    },

    preview$: renderMethod('preview'),

    render$: renderMethod('render'),

    commit$() {
      const doc = this.document;
      return doc ? doc.commit$() : this.update$('commit');
    },

    revert$() {
      const doc = this.document;
      return doc ? doc.revert$() : resolve();
    }
  };
}


const updateCacheAt = function(figure,updater,target) {
  const cache = figure._cache;

  // pre metrics are kept

  // metrics are kept if the target of the transform
  // is not the figure being update
  if( figure === target ) {
    cache.update('matrix'        ,updater,'transform'     );
    cache.update('vectorBounds'  ,updater,'vectorBounds'  );
    cache.update('bounds'        ,updater,'bounds'        );
    cache.update('materialBounds',updater,'materialBounds');
  }

  // global pre metrics are kept if the figure is not grouped, because
  // they are the same as pre metrics in this case
  if( get(target._owner,'isGroup') ) {
    cache.update('globalPreMatrix'      ,updater,'transform'   );
    cache.update('globalPreVectorBounds',updater,'vectorBounds');
    cache.update('globalPreBounds'      ,updater,'bounds'      );
  }

  cache.update('globalMatrix'        ,updater,'transform'     );
  cache.update('globalVectorBounds'  ,updater,'vectorBounds'  );
  cache.update('globalBounds'        ,updater,'bounds'        );
  cache.update('globalMaterialBounds',updater,'materialBounds');
};

const updateChildsCache = function(group,updater,target) {
  const gfs = group._figures;
  //_.forEach(gfs, function(f) {
  for(let k=0,kEnd=gfs.length;k<kEnd;++k) {const f =gfs[k];
    updateCacheAt(f,updater,target);
    if( f.isGroup ) {
      updateChildsCache(f,updater,target);
    }
  }//);
};

const clearParentsCache = function( figure ) {
  // Clear parents caches
  let owner = figure._owner;
  while( get(owner,'isGroup') ) {
    owner._cache.clear(); // Clear group cache
    owner = owner._owner;
  }
};

export const updateCache = function(figure,updater) {

  clearParentsCache(figure);

  // Update target
  updateCacheAt(figure,updater,figure);

  if( figure.isGroup ) {
    updateChildsCache(figure,updater,figure);

    // Review, updating output figure if it is a group
    const of = figure._outputFigure;
    if( of !== figure && get(of,'isGroup') ) {
      updateChildsCache(of,updater,figure);
    }
  }
};

const _clearInvalidatedFigureLocalCache = function( figure ) {
  figure._cache.clear();
};
const _clearInvalidatedFigureCache = function( figure ) {
  figure.deepForEachFigure(_clearInvalidatedFigureLocalCache);
};

const clearInvalidatedCache = function( figure ) {
  clearParentsCache(figure);
  _clearInvalidatedFigureCache(figure);

  // Review, updating output figure if it is a group
  const of = figure._outputFigure;
  if( of !== figure && get(of,'isGroup') ) {
    _clearInvalidatedFigureCache(of);
  }
};

const _clearGroupedFigureLocalCache = function( figure ) {
  const cache = figure._cache;
  cache.clear('globalPreMatrix');
  cache.clear('globalPreVectorBounds');
  cache.clear('globalPreBounds');
  cache.clear('globalMatrix');
  cache.clear('globalVectorBounds');
  cache.clear('globalBounds');
  cache.clear('globalMaterialBounds');
};
const _clearGroupedFigureCache = function( figure ) {
  figure.deepForEachFigure(_clearGroupedFigureLocalCache);
};

const clearGroupedCache = function( figure ) {
  clearParentsCache(figure);
  _clearGroupedFigureCache(figure);

  // Review, updating output figure if it is a group
  const of = figure._outputFigure;
  if( of !== figure && get(of,'isGroup') ) {
    _clearGroupedFigureCache(of);
  }
};

// This updater modifies all the cached metrics
const TranslateCacheUpdater = function(dx,dy) {
  this.dx = dx; this.dy = dy;
  this.translate = Matrix.translate(dx,dy);
};
TranslateCacheUpdater.prototype = {
  vectorBounds( v ) {
    return v.translate(this.dx,this.dy);
  },
  bounds( v ) {
    return v.translate(this.dx,this.dy);
  },
  transform( v ) {
    return Matrix.m(this.translate,v);
  }
};

// This updater modifies all the cached metrics if sx === sy
const ScaleFromCacheUpdater = function(point, sx, sy, deg) {
  // Do not update bounds
  if( sx !== sy ) {
    this.vectorBounds = null;
    this.bounds = null;
  }
  this.scaling = Matrix.scaleFrom(point, sx, sy, deg) ;
};
ScaleFromCacheUpdater.prototype = {
  vectorBounds( v ) { return v.scalingTransform(this.scaling); },
  bounds( v ) { return v.scalingTransform(this.scaling); },
  transform( v ) { return Matrix.m( this.scaling, v ); }
};

// Bounds needs to be recalculated after applying this updater
const TransformCacheUpdater = function(matrix) {
  this.matrix = matrix;
};
TransformCacheUpdater.prototype = {
  transform( v ) { return Matrix.m( this.matrix, v ); }
};


let measureFigure$ = function( figure ) {

  // console.log('Clean up cache, ' + broadcastInfo.figureInvalidationState);

  updateCache(figure,{}); // Clean up the post-transform cache, preMetrics are kept

  const of = figure._outputFigure;
  if( figure !== of ) {
    of.pin = figure.pin.clone();
    of.matrix22 = figure._matrix22.clone();
  }

  return resolve(figure);
};

function setSimpleFigureOutput( figure ) {
  if( figure.isEmpty ) {
    figure.outputFigure = figure.createEmptyOutput();
  } else {
    if( Config.avoidClonningInputForPrimitives ) {
      // Optimization, the only important thing is to remember to avoid transforming
      // the figure twice. The code uses ._aliasedOutput() because of this.
      figure.outputFigure = figure;
    } else {
      figure.outputFigure = figure.clone(false);
    }
    if( figure.isGroup ) {
      forEach(figure._figures,setSimpleFigureOutput);
    }
  }
}

// [TODO] We should only call generateOutputFigure$ here
//        each figure needs to take care of the output generation
//        or in any case, the if condition should be inverted

let processFigure$ = function( figure ) {
  // console.log('localProcessing');

  if( figure.isText ||
      figure.isRaster ||
      figure._processes.length > 0 ||
      !figure._preMatrix22.isIdentity() ||
      ( figure.isGroup && ! figure.onlySimpleShapes() ) ) {

    return figure.generateOutputFigure$() .then( outputFigure => {

    // We need to recompute all the metrics
    clearInvalidatedCache(figure);
    figure.outputFigure = outputFigure;
    return figure;

    });
  }
  else {

    setSimpleFigureOutput(figure);
    clearInvalidatedCache(figure);
    return resolve( figure );
  }

};

export const InvalidationStates = {

// Figure invalidation states
//=================================================================================================
// A Figure needs to be processed to generate its output representation.
// In the case of a Text, for example, we need to get the letters curves from the font and
// compose the proper line. If the figure has effects (processes) we need to apply them to the
// initial representation in order to produce the deformations (envelope) or add new curves to
// create the output (contour).
// Finally this representation is transformed and placed in the document using the figure transform
// and pin.
// We call this 'processing' the figure.
// Canvas API needs to know some basic measurements (bounds, matrix) in order to render the
// figure representation in the browser.
// We call this 'measuring' the figure.
//
// Figure --[ Processing ]--> Processed Figure --[ Measuring ]--> (bounds, matrix)
//
// Processing the figure can be quite expensive and except for simple shapes it requires a call to
// the server. We need to cache the result of the processing stage to avoid having to re-do it
// every time we interact with the figure. The Processor object takes care of this task
// generating a processed figure (output representation) for the figure it is attached to.
// Measuring the figure requires a bounding box computation that is also expensive but that we are
// able to do it locally avoiding the net latency. This measurements also needs to be cached
// because they are accesed often by Canvas API and the clients. They are stored in the cache of
// the input figure. The cache of the output figure is linked to this cache to avoid unnecesary
// computations.
// If we only change the pin or the transform, there is no needs to re-do the processing stage and
// only a measurement stage must be issued.
// There are cases where we can even update the cached processed figure and its measurement (for
// example, when translating the figure) and then we can avoid both expensive stages.

  figure : {

  // all
  //----------------------------------------------------------------------------------------------
  // The figure needs to be processed and measured. This will be done in the server or locally
  // depending on the complexity of the figure.
    all           : 2,

  // transform
  //----------------------------------------------------------------------------------------------
  // The figure has been processed and only the pin and transformation has changed, so we only
  // need to recompute the measurements (bounds, matrix).
    transform     : 1,

  // none
  //----------------------------------------------------------------------------------------------
  // The figure is in a valid state. The figure was processed and cached in the server and
  // we now have an up-to-date cache Id to talk to the server about it -or- there is an
  // up-to-date processed figure object locally.
  // The figure measurements values are also up-to-date (bounds, matrix)
    none          : 0

  },

// Views invalidation states
//=================================================================================================
// Independently of what the figure invalidation state is, the figure needs to be rendered to the
// screen. This is done with figure views, that are representations of the figure on the screen
// canvas with a certain viewport state (zoom, offset) and rendering params (wireframe).
// The Processor can be called with a render or a cache command. We maintain a view
// invalidation state that is broadcasted and listened by the views, that update themselves
// accordingly. When the processor is call with a cache call, the view invalidation state is not
// changed and in the next call to render it will be properly broadcasted to the views.
// It is important that the figure invalidation state and the view invalidation state (the one that
// will be broadcasted) are maintained separately to allow optimizations. For example, when we
// perform a color replacement operation and the figure can be rendered locally there is no need
// to do expensive server calls. We can just replace the color in the cached processed figure
// locally and set the view invalidation state to 'all'. The figure invalidation state should
// be 'none' because both the output representation and the measurements are already up-to-date.
// This scheme let us inform the views that a refresh is in place and at the same time tell the
// processor that there is no need to recompute anything.
// Another example is the optimization implemented when scaling the figure. In this case we can
// also just update the output representation and update the measurements directly. Again the
// figure invalidation state should be set to 'none' but the view invalidation state should be
// 'transform' to inform the views of what we have changed. In the old JS engine this was done
// with an intermidiate figure invalidation state called 'scale', but then it was not possible
// to properly implement optimizations like the color replacement because that doesn't fit in
// a linear invalidation state hierarchy (figure invalidation should be none and view invalidation
// must be all).

  views : {

  // all
  //----------------------------------------------------------------------------------------------
  // The figure representation needs to be completely updated.
    all           : 3,

  // transform
  //----------------------------------------------------------------------------------------------
  // The pre output representation is up-to-date and only the final transformation needs
  // to be updated. This is important when using local rendering because generating the the raphael
  // representation can be expensive.
    transform     : 2,

  // viewport
  //----------------------------------------------------------------------------------------------
  // When the viewport params are changed, zoom or pan offset we can directly provide visual
  // feedback before asking for the image in the case of server rendering. For local rendering
  // it is conceptually cleaner also to only update the viewport transformation and leave the
  // figure transform as it is.
    viewport      : 1,

  // none
  //----------------------------------------------------------------------------------------------
  // The figure view only will be updated if this is the first time it is being rendered.
  // If not, it should be showing an up-to-date representation of the figure.
    none          : 0

  },

  upgrade ( type,oldState,newState) {
    return ( InvalidationStates[type][oldState] > InvalidationStates[type][newState] ) ? oldState : newState ;
  },
  downgrade ( type,oldState,newState) {
    return ( InvalidationStates[type][oldState] < InvalidationStates[type][newState] ) ? oldState : newState ;
  }
} ;

export const Processor = Base.extend({ typeName: 'Processor',

  // render, updates the figure and fire a render event broadcasting invalidation information for the
  // views to update themselves.

  // cache, same as update but the figure will be forced to be cached on the server. Use this function
  // if you want to externally use the cacheId. The Designer2C is currently using this because it draws
  // figures with a direct call to the server for some dialogs instead of using a Canvas API figure view.

  // If the figure is modified, these functions should be called to invalidate the output representation

  invalidate() {
    clearInvalidatedCache(this.figure);
    this._invalidate();
  },
  _invalidate() {
    this.figureInvalidationState = InvalidationStates.upgrade('figure',this.figureInvalidationState,'all');
    this.figure.lock();
    // g.log('Processor.invalidate > ' + this.figureInvalidationState);
    this._invalidateView();
  },

  invalidateGrouping() {
    if( this.figure._aliasedOutput() ) {
      clearGroupedCache(this.figure);
      this._invalidateGrouping();
    }
    else {
      // For normal processors, we need to recalculate the output
      this.invalidate();
    }
  },
  _invalidateGrouping() {
    if( ! ( Config.computeFigureGeometryLocally && this.figure.supportsLocalMetrics() ) ) {
      this._invalidateTransform();
    }
  },

// If only the pin or the transform are modified, this function should be called and only
// the measurements will be re-computed.

  invalidateTransform() {
    updateCache(this.figure,{});
    this._invalidateTransform();
  },
  _invalidateTransform() {
    this.figureInvalidationState = InvalidationStates.upgrade('figure',this.figureInvalidationState,'transform');
    // g.log('Processor.invalidateTransform > ' + this.figureInvalidationState);
    this._invalidateViewTransform();
  },

  computePrePin() { const of = this.figure._outputFigure; return of ? of._primitive_computePrePin() : error('Using metrics without calling update$'); },

  computePreReferencePoint() { const of = this.figure._outputFigure; return of ? of._primitive_computePreReferencePoint() : error('Using metrics without calling update$'); },

  computePreVectorBounds     () { const of = this.figure._outputFigure; return of ? of._primitive_computePreVectorBounds()   : error('Using metrics without calling update$'); },
  computePreBounds           () { const of = this.figure._outputFigure; return of ? of._primitive_computePreBounds()         : error('Using metrics without calling update$'); },

  computeVectorBounds        () { const of = this.figure._outputFigure; return of ? of._primitive_computeVectorBounds()   : error('Using metrics without calling update$'); },
  computeBounds              () { const of = this.figure._outputFigure; return of ? of._primitive_computeBounds()         : error('Using metrics without calling update$'); },

  computeMaterialBounds      () { var of = this.figure._outputFigure; return of ? of._primitive_computeMaterialBounds()     : error('Using metrics without calling update$'); },

  computeGlobalPreVectorBounds() { const of = this.figure._outputFigure; return of ? of._primitive_computeGlobalPreVectorBounds()   : error('Using metrics without calling update$'); },
  computeGlobalPreBounds     () { const of = this.figure._outputFigure; return of ? of._primitive_computeGlobalPreBounds()         : error('Using metrics without calling update$'); },

  computeGlobalVectorBounds  () { const of = this.figure._outputFigure; return of ? of._primitive_computeGlobalVectorBounds()   : error('Using metrics without calling update$'); },
  computeGlobalBounds        () { const of = this.figure._outputFigure; return of ? of._primitive_computeGlobalBounds()         : error('Using metrics without calling update$'); },

  computeGlobalMaterialBounds() { const of = this.figure._outputFigure; return of ? of._primitive_computeGlobalMaterialBounds()         : error('Using metrics without calling update$'); },

  computePreVectorBoundsAfter ( transform ) { const of = this.figure._outputFigure; return of ? of._primitive_computePreVectorBoundsAfter(transform) : error('Using metrics without calling update$'); },
  computePreBoundsAfter       ( transform ) { const of = this.figure._outputFigure; return of ? of._primitive_computePreBoundsAfter(transform)       : error('Using metrics without calling update$'); },

  fineHitTest( point, transform, options ) {
    const of = this.figure._outputFigure; return of ? of._fineHitTest(point,transform,options) : true;
  },
  representationType() {
    const of = this.figure._outputFigure;
    return of ? of._representationType() : 'hidden';
  },

  draw( context, transform, config ) {
    const { outputFigure } = this.figure;
    if( outputFigure ) {
      outputFigure._draw(context,transform,config);
    }
  },

// Some modifications on the figure are routed through more fine grained operations that will possible
// avoid expensive processing or measuring stages by direct manipulation of the local figure output representation.

  fastTranslate( dx, dy ) {
    // g.log('Processor.fastTranslate');
    const f = this.figure;
    updateCache(f,new TranslateCacheUpdater(dx,dy));

    const of = f._outputFigure;
    if( of && f !== of ) {
      of.translate(dx,dy);
    }
    // In the case of server rendering it is an important optimization to be able
    // to avoid requesting a new image from the server and in the case we have moved an
    // integer number of pixels we do not need to do it
    // So there is nothing to invalidate here except when currently processing
    if( this._currentlyProcessing || this.invalidated() ) {
      // console.log('fastTranslate, while processing');
      this.invalidateTransform();
    }
    else if( f.needsFixedDrawing() ) {
      this.invalidateView();
    }
  },

  translate( dx, dy ) {
    // g.log('Processor.translate');
    const f = this.figure;
    updateCache(f,new TranslateCacheUpdater(dx,dy) );

    const of = f._outputFigure;
    if( of && f !== of ) {
      of.translate(dx,dy);
    }

    // We need to retrieve the image again because the subpixels may be wrong
    // If you now that the image does not need to be updated use fastTranslate
    this.invalidateViewTransform();
  },

  scaleFrom( point, sx, sy, deg ) {
    // g.log('Processor.scaleFrom');
    const f = this.figure;
    updateCache(f,new ScaleFromCacheUpdater(point,sx,sy,deg) );

    const of = f._outputFigure;
    if( of && f !== of ) {
      of.scaleFrom(point,sx,sy,deg);
    }

    if( sx === sy ) {
      this.invalidateViewTransform();
    }
    else {
      this.invalidateUpdatedTransform();
    }
  },

  transform( matrix ) {
    const f = this.figure;
    updateCache(f, new TransformCacheUpdater(matrix) );

    const of = f._outputFigure;
    if( of && f !== of ) {
      of.transform(matrix);
    }

    this.invalidateUpdatedTransform();

    // [TODO] Remove when bounds based pinning is gone
    if( ! f._pin.anchorBased() ) {
      f._fixUpPin$('referencePoint_');
    }
  },
  invalidateChildren( aFigure ) {
    aFigure.invalidate();
    if (aFigure.isGroup) {
      let figures = aFigure._figures;
      for(let i=0,iEnd=figures.length;i<iEnd;++i) {
        this.invalidateChildren(figures[i] );
      }
    }
  },
  replaceColor( oldColor, newColor, options ) {
    const f = this.figure;

    const changed = f._replaceColor(oldColor,newColor,options);
    if( ! changed ) { return; } // nothing to invalidate

    //we need to invalidate the children or text inside groups is not updated
    if (f.isGroup) {
      this.invalidateChildren(f);
    }

    if( f._powerClipContents.length > 0 ) {
      const pFigures = f._powerClipContents[0]._figures;
      for(let k=0,kEnd=pFigures.length;k<kEnd;++k) {
        const figure = pFigures[k];
        figure.cacheId = null;
        figure.invalidate(); // [TODO] Optimize
      }
    }

    if( Config.replaceColorsLocallyWhenPossible &&
        Config.computeFigureGeometryLocally     &&
        f.supportsLocalMetrics()              && // We can not longer use the cached figure,
        this.representationType() === 'local' )  // so we need this optimization enabled
    {
      const of = f._outputFigure;
      if( of && f !== of ) {
        of._replaceColor(oldColor,newColor,options);
      }
      // We are touching the processed figure directly, we can no longer
      // use the stored cacheId figure in the server
      f.cacheId = null;
      f.lock();
      this.invalidateView();
    }
    else {
      this.invalidate();
    }
  },

  replaceBrush( oldBrush, newBrush ) {
    const f = this.figure;

    const changed = f._replaceBrush(oldBrush,newBrush);
    if( ! changed ) { return; } // nothing to invalidate

    //we need to invalidate the children or text inside groups is not updated
    if (f.isGroup) {
      this.invalidateChildren(f);
    }

    if( f._powerClipContents.length > 0 ) {
      const pFigures = f._powerClipContents[0]._figures;
      for(let k=0,kEnd=pFigures.length;k<kEnd;++k) {
        const figure = pFigures[k];
        figure.cacheId = null;
        figure.invalidate(); // [TODO] Optimize
      }
    }

    if( Config.replaceColorsLocallyWhenPossible &&
        Config.computeFigureGeometryLocally     &&
        f.supportsLocalMetrics()                && // We can not longer use the cached figure,
        this.representationType() === 'local' )    // so we need this optimization enabled
    {
      const of = f._outputFigure;
      if( of && f !== of ) {
        of._replaceBrush(oldBrush,newBrush);
      }
      // We are touching the processed figure directly, we can no longer
      // use the stored cacheId figure in the server
      f.cacheId = null;
      f.lock();
      this.invalidateView();
    }
    else {
      this.invalidate();
    }
  },

// We can add a more general function here later
  setTaggedColors ( tag  , color ) { this._setColors('_setTaggedColors' ,tag  ,color); },
  setLabeledColors( label, color ) { this._setColors('_setLabeledColors',label,color); },

  _setColors( forward,key, color ) {
    const f = this.figure;

    const changed = f[forward](key,color);
    if( ! changed ) { return; } // nothing to invalidate

    //we need to invalidate the children or text inside groups is not updated
    if (f.isGroup) {
      this.invalidateChildren(f);
    }
    if( f._powerClipContents.length > 0 ) {
      const pFigures = f._powerClipContents[0]._figures;
      for(let k=0,kEnd=pFigures.length;k<kEnd;++k) {
        const figure = pFigures[k];
        figure.cacheId = null;
        figure.invalidate(); // [TODO] Optimize
      }
    }

    if( Config.replaceColorsLocallyWhenPossible &&
        Config.computeFigureGeometryLocally     &&
        f.supportsLocalMetrics()              && // We can not longer use the cached figure,
        this.representationType() === 'local' )  // so we need this optimization enabled
    {
      const of = f._outputFigure; // [TODO] Review
      if( of && f !== of ) {
        of[forward](key,color);
      }
      // We are touching the processed figure directly, we can no longer
      // use the stored cacheId figure in the server
      f.cacheId = null;
      f.lock();
      this.invalidateView();
    }
    else {
      this.invalidate();
    }
  },

  clone( otherOwner, partOfGroup, ungrouping ) {
    const f = this.figure;
    const np = cloneProcessor(Processor,this,otherOwner,partOfGroup,ungrouping);
    // np.viewsInvalidationState is 'all', to force the rendering in the first draw
    np.figureInvalidationState = this.currentlyProcessing() ? 'all' : this.figureInvalidationState;
    otherOwner.cacheId = f._cacheId;
    const of = f._outputFigure;
    if( of ) {
      let otherOutputFigure;
      if( f === of ) {
        otherOutputFigure = otherOwner;
      }
      else {
        // We need to rewire the PowerClipLink to point to the new figure contents
        // This uses a cloneLink hook, that seems to be the fastest way to rewire
        // power clip links, we are not cleaning the links afterwards. We may do it
        // later to reduce memory consumption, but looks fine for the moment and
        // we are prefering speed and simplicity here.
        // Check clone functions for PowerClipContent and PowerClipLink
        otherOutputFigure = of.clone(false);
      }
      otherOwner.outputFigure = otherOutputFigure;
    }
    return np;
  },

  currentlyProcessing() { return this._currentlyProcessing > 0; },

  invalidated() { return this.figureInvalidationState !== 'none' || this.processingFigureInvalidationState !== 'none'; },

  processed() { return this.figure._outputFigure !== null; },

  // internal functions

  init(ownerFigure) {

    this.figure = ownerFigure ;

    this._lastPromise = resolve();

    this._currentlyProcessing = 0;

    // Read documentation at InvalidationStates

    // Invalidation state of the figure and its processed figure representation
    this.figureInvalidationState = 'all' ;
    this.processingFigureInvalidationState = 'none';

    // Invalidation state that will be broadcasted to the views so they get properly updated
    this.viewsInvalidationState  = 'all' ;
  },

  invalidateView(force) {
    // Disable optimizations if we are currently processing
    if( !force && (this._currentlyProcessing || this.invalidated()) ) {
      this.invalidate();
    }
    else {
      this._invalidateView();
    }
  },

  invalidateUpdatedTransform() {
    this._invalidateUpdatedTransform();
  },
  _invalidateUpdatedTransform() {
    // This function is called every time an updater performs a partial update of cached metrics
    // If we can not recompute the dropped ones locally, then we need to ask them to the server
    if( Config.computeFigureGeometryLocally &&
        this.figure.supportsLocalMetrics() ) {

      // Disable optimizations if we are currently processing
      if( this._currentlyProcessing || this.invalidated() ) {
        // console.log('Currently processing, hard invalidate transform');
        this.invalidateTransform();
      }
      else {
        this.invalidateViewTransform();
      }
    }
    else {
      this._invalidateTransform();
    }
  },

  invalidateViewTransform() {
    // Disable optimizations if we are currently processing
    if( this._currentlyProcessing || this.invalidated() ) {
      // console.log('Currently processing, hard invalidate transform');
      this.invalidateTransform();
    }
    else {
      this._invalidateViewTransform();
    }
  },

  _invalidateView() {
    this.viewsInvalidationState = InvalidationStates.upgrade('views', this.viewsInvalidationState, 'all');
    // g.log('Processor.invalidateView > ' + this.viewsInvalidationState);

    const pccs = this.figure.deepPowerClipContents();
    for( let k = 0, kEnd = pccs.length; k < kEnd; ++ k ) {
      const figures = pccs[k]._figures;
      for( let n = 0, nEnd = figures.length; n < nEnd; ++ n ) {
        figures[n].invalidateView();
      }
    }
  },

  _invalidateViewTransform() {
    this.figure.lock();
    this.viewsInvalidationState = InvalidationStates.upgrade('views', this.viewsInvalidationState, 'transform');
    // g.log('Processor.invalidateViewTransform > ' + this.viewsInvalidationState );
  },

  // Queue process calls. Application of processing will always respect order
  // of update request even if old request require longer time than new ones.

  update$( type ) {

    if( type === 'preview' &&
        this._currentlyProcessing === 0 &&
        this.figureInvalidationState === 'none' &&
        this.viewsInvalidationState === 'none' ) {
      return resolve(this.figure);
    }

    const deferred = new Deferred();

    const processing = () => {

      ++ this._currentlyProcessing;

      const { figureInvalidationState, viewsInvalidationState } = this;
      const broadcastInfo = { figureInvalidationState, viewsInvalidationState };

      this.fireProcessingStarted(type,broadcastInfo);

      return this.doProcessing(type) .then( () => {

      return this.fireProcessingSuccess(type,broadcastInfo);

      }).then( () => {

      -- this._currentlyProcessing;
      deferred.resolve( this.figure );

      }).then(null, (error) => {
      console.log(error);

      -- this._currentlyProcessing;
      deferred.reject( this.fireProcessingFailure(type,broadcastInfo) );

      });
    };

    // Queue completion of processing after last promise is done
    this._lastPromise .then(processing,processing);

    return ( this._lastPromise = deferred.promise );
  },

  doProcessing( type ) {

    let { figure, figureInvalidationState } = this;

    // Ignore preview requests if metrics can not be processed locally, like
    // in the case of ProxyGroups, the result is still a succesful update for
    // the rendering code. Bounds will be computed later in the next update,
    // render or commit.
    if( type === 'preview' && ! figure.supportsLocalMetrics() ) {
      return resolve();
    }

    // g.log('Processor.doProcessing, type: ' + type + ', fis: ' + this.figureInvalidationState + ', vis: ' + this.viewsInvalidationState );

    // This is only used for invalidated()
    this.processingFigureInvalidationState = figureInvalidationState ;

    // The figure will be up-to-date after the processing. In case it fails, the state is reverted to
    // the current processing state. We have to do it this way because further invalidation calls can be
    // issued while processing (mainly when requesting a full processing to the server)
    this.figureInvalidationState = 'none' ;

    const drawingRequest = isDrawingRequest(type);
    if( drawingRequest ) {
      // Same as above, in the case the processing fails this will be reverted to the processing state
      this.viewsInvalidationState = 'none' ;
    }

    const handleInvalidateInTheServer = ( figureInvalidationState === 'all' &&
                                        ( ! ( Config.processLocallyWhenPossible && figure.canBeProcessedLocally() ) ||
                                              ! Config.renderLocallyWhenPossible ||
                                              ! Config.computeFigureGeometryLocally ||
                                              ! figure.supportsLocalMetrics()  ) &&
                                          ! figure.isEmpty );

    if( handleInvalidateInTheServer ) {
      figure.fireEvent('serverinvalidate');
    }
    const processUsingServer =  handleInvalidateInTheServer ||
                            ( type === 'cache' && ( figureInvalidationState === 'all' || ! figure._cacheId ) ) ||
                            ( drawingRequest && figure.needsCacheToRender() && ! figure._cacheId );


    // Process and meassure figure using Grafika or Core

    if( processUsingServer ) {

      // The figure will be processed and cached in the server
      return Figure.processUsingServer$(figure,{
        getOutput: ! figure.isProxyGroup,
        storeInCache: type === 'cache' ||
                      ! Config.computeFigureGeometryLocally  ||
                      ! figure.supportsLocalMetrics()        ||
                      ! Config.avoidCacheWhenRenderLocally   ||
                        figure.needsCacheToRender()          ||
                        Config.renderLocallyWhenPossible
      });
    }
    else if( figureInvalidationState === 'all' ) {

      // The figure can be processed locally
      return processFigure$(figure);
    }
    else if( figureInvalidationState === 'transform' ) {

      // Measurements (bounds, matrix) needs to be updated

      if( Config.computeFigureGeometryLocally &&
          figure.supportsLocalMetrics() &&
          figure._outputFigure ) {

        return measureFigure$(figure);
      }
      else {
        return Figure.measureUsingCore$(figure);
      }
    }
    else {
      return resolve();
    }
  },

  fireProcessingSuccess( type, broadcastInfo ) {
    const figure = this.figure;
    const response = { type, figure, broadcastInfo } ;

    // Process PowerClip Input...
    return figure.processPowerClips$() .then( () => {

    this.processingFigureInvalidationState='none';

    figure.fireEvent('processingcomplete', response);

    if( isDrawingRequest(type) ) {

      // We need to review this... it is needed only when using server processing
      // We can avoid some processing calls by manually setting up PowerClipLinks
      // in the server output figure response for simple most common cases (Text)
      //==========================================================================
      const of = figure._outputFigure;
      if( of && of !== figure &&
          ! figure.usingPowerClipLink() ) { // Directly using PowerClipLink

        const pccs = of.deepPowerClipContents();

        if( pccs.length > 0 ) {

          return map$( pccs, pcc => invokeMap$(pcc._figures,'render$') ) .then( () => {

          // console.log('PC after render');

          figure.fireEvent('rendercomplete', response);
          return figure;

          });
        }
      }
      //==========================================================================

      // Normal flow
      figure.fireEvent('rendercomplete', response);
    }

    return figure;
    });
  },

  fireProcessingFailure( type, broadcastInfo, errorMessage ) {
    const { figure } = this;
    const response = { errorMessage, type, figure, broadcastInfo } ;

    // g.log('processing failure');
    this.figureInvalidationState = InvalidationStates.upgrade('figure',this.figureInvalidationState,broadcastInfo.figureInvalidationState) ;

    figure.fireEvent('processingfailed', response);

    if( isDrawingRequest(type) ) {
      this.viewsInvalidationState = InvalidationStates.upgrade('views',this.viewsInvalidationState,broadcastInfo.viewsInvalidationState) ;
    }

    return response;
  },

  fireProcessingStarted( type, broadcastInfo ) {
    const { figure } = this;
    const response = { broadcastInfo, figure, type };
    figure.fireEvent('processingstarted', response );
  }
});


export const GroupProcessor = Base.extend({ typeName: 'GroupProcessor',

  update$( type ) {
    const { figure } = this;
    figure.fireEvent('processingstarted');
    return map$(figure._figures, figure => figure.update$(type) ) .then( () => {

      const broadcastInfo = { figureInvalidationState: 'none',
                              viewsInvalidationState : 'none' };
      const response = { figure, broadcastInfo, type } ;

      figure.fireEvent('processingcomplete',response);
      figure.fireEvent('rendercomplete',response);
    });
  },

  computePrePin() {
    return this.figure._primitive_computePrePin();
  },

  computePreReferencePoint() {
    return this.figure._primitive_computePreReferencePoint();
  },

  computePreVectorBounds() {
    let figure = this.figure, figures = figure._figures;
    if( figure.isGrouped ) {
      return Figures.vectorBounds_(figures);
    }
    else {
      return Figures.globalPreVectorBounds_(figures);
    }
  },
  computePreBounds() {
    let figure = this.figure, figures = figure._figures;
    if( figure.isGrouped ) {
      return Figures.bounds_(figures);
    }
    else {
      return Figures.globalPreBounds_(figures);
    }
  },
  computeVectorBounds() {
    let figure = this.figure;
    if( figure.isGrouped ) {
      return figure._primitive_computeVectorBounds();
    }
    else {
      return Figures.globalVectorBounds_(figure._figures);
    }
  },
  computeBounds() {
    let figure = this.figure;
    if( figure.isGrouped ) {
      return this.figure._primitive_computeBounds();
    }
    else {
      return Figures.globalBounds_(this.figure._figures);
    }
  },

  computeMaterialBounds() {
    let figure = this.figure;
    if( figure.isGrouped ) {
      return this.figure._primitive_computeMaterialBounds();
    }
    else {
      return Figures.globalMaterialBounds_(this.figure._figures);
    }
  },

  computeGlobalPreVectorBounds() { return Figures.globalPreVectorBounds_(this.figure._figures); },
  computeGlobalPreBounds    () { return Figures.globalPreBounds_(this.figure._figures); },

  computeGlobalVectorBounds() { return Figures.globalVectorBounds_(this.figure._figures); },
  computeGlobalBounds    () { return Figures.globalBounds_(this.figure._figures); },

  computeGlobalMaterialBounds() { return Figures.globalMaterialBounds_(this.figure._figures); },

  computePreVectorBoundsAfter( transform ) { return this.figure._primitive_computePreVectorBoundsAfter(transform); },
  computePreBoundsAfter      ( transform ) { return this.figure._primitive_computePreBoundsAfter(transform); },

  fineHitTest(/*point,transform,options*/) {
    return true; //[TODO]
  },
  representationType() {
    return 'local'; //[TODO]
  },
  draw( context, transform, config ) {
    const figures = this.figure._figures;
    //.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {const f = figures[k];
      f.draw(context,transform,config);
    }//);
  },

  currentlyProcessing() {
    return Figures.currentlyProcessing(this.figure.figures);
  },
  invalidated() {
    return Figures.invalidated(this.figure.figures);
  },
  processed() {
    return every( this.figure.figures, f => f.processed() );
  },

  invalidate() {
    clearInvalidatedCache(this.figure);
    this._invalidate();
  },
  _invalidate() {
    const figures = this.figure._figures;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k]._processor._invalidate();
    }//);
  },

  invalidateGrouping() {
    this.figure.lock();
    clearGroupedCache(this.figure);
    this._invalidateGrouping();
    // g.log('Processor.invalidateTransform > ' + this.figureInvalidationState);
  },
  _invalidateGrouping() {
    const figures = this.figure._figures;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k]._processor._invalidateGrouping();
    }//);
  },

  invalidateTransform() {
    this.figure.lock();
    updateCache(this.figure,{});
    this._invalidateTransform();
    // g.log('Processor.invalidateTransform > ' + this.figureInvalidationState);
  },
  _invalidateTransform() {
    const figures = this.figure._figures;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k]._processor._invalidateTransform();
    }//);
  },

  invalidateView() {
    invokeMap(this.figure._figures,'invalidateView');
    // g.log('Processor.invalidateView > ' + this.viewsInvalidationState);
  },

  invalidateUpdatedTransform() {
    this._invalidateUpdatedTransform();
  },
  _invalidateUpdatedTransform() {
    const figures = this.figure._figures;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k]._processor._invalidateUpdatedTransform();
    }//);
    // g.log('Processor.invalidateViewTransform > ' + this.viewsInvalidationState );
  },

  invalidateViewTransform() {
    this.figure.lock();
    invokeMap(this.figure._figures,'invalidateViewTransform');
    // g.log('Processor.invalidateViewTransform > ' + this.viewsInvalidationState );
  },

  fastTranslate( dx, dy ) {
    updateCache(this.figure, new TranslateCacheUpdater(dx,dy));
    if( this.invalidated() ) {
      this.invalidateTransform();
    }
  },

  translate( dx, dy ) {
    updateCache(this.figure, new TranslateCacheUpdater(dx,dy));
    this.invalidateViewTransform();
  },

  scaleFrom( point, sx, sy, deg ) {
    updateCache(this.figure, new ScaleFromCacheUpdater(point,sx,sy,deg));
    if( sx === sy ) {
      this.invalidateViewTransform();
    }
    else {
      this.invalidateUpdatedTransform();
    }
  },

  transform( matrix ) {
    updateCache(this.figure, new TransformCacheUpdater(matrix));
    this.invalidateUpdatedTransform();
  },

  replaceColor( oldColor, newColor, options ) {
    const { figures } = this.figure;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].replaceColor(oldColor,newColor,options);
    }//);
  },

  replaceBrush( oldBrush, newBrush ) {
    const { figures } = this.figure;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].replaceBrush(oldBrush,newBrush);
    }//);
  },

  setTaggedColors( tag, color ) {
    const { figures } = this.figure;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].setTaggedColors(tag,color);
    }//);
  },

  setLabeledColors( label, color ) {
    const { figures } = this.figure;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].setLabeledColors(label,color);
    }//);
  },

  clone( otherOwner, partOfGroup, ungrouping ) {
    return cloneProcessor(GroupProcessor,this,otherOwner,partOfGroup,ungrouping);
  },

// internal functions

  init( ownerFigure ) {
    this.figure = ownerFigure ;
  }
});


export const OutputProcessor = Base.extend({ typeName: 'OutputProcessor',
  render$      () { error('error'); },
  commit$      () { error('error'); },
  update$      () { error('error'); },
  cache$       () { error('error'); },

  computePreReferencePoint   () { return this.figure._primitive_computePreReferencePoint(); },

  computePrePin              () { return this.figure._primitive_computePrePin(); },

  computePreVectorBounds     () { return this.figure._primitive_computePreVectorBounds(); },
  computePreBounds           () { return this.figure._primitive_computePreBounds(); },

  computeVectorBounds        () { return this.figure._primitive_computeVectorBounds(); },
  computeBounds              () { return this.figure._primitive_computeBounds(); },

  computeMaterialBounds() {
    return this.figure._primitive_computeMaterialBounds();
  },

  computeGlobalPreVectorBounds() { return this.figure._primitive_computeGlobalPreVectorBounds(); },
  computeGlobalPreBounds     () { return this.figure._primitive_computeGlobalPreBounds(); },

  computeGlobalVectorBounds  () { return this.figure._primitive_computeGlobalVectorBounds(); },
  computeGlobalBounds        () { return this.figure._primitive_computeGlobalBounds(); },

  computeGlobalMaterialBounds() { return this.figure._primitive_computeGlobalMaterialBounds(); },

  computePreVectorBoundsAfter( transform ) { return this.figure._primitive_computePreVectorBoundsAfter(transform); },
  computePreBoundsAfter      ( transform ) { return this.figure._primitive_computePreBoundsAfter(transform); },

  fineHitTest( point, transform, options ) {
    return this.figure._fineHitTest(point,transform,options);
  },
  representationType() { return this.figure._representationType(); },

  draw( context, transform, config ) {
    const { outputFigure } = this.figure;
    if( outputFigure ) {
      outputFigure._draw(context,transform,config);
    }
  },

  invalidate() {},
  _invalidate() {},
  invalidateTransform() {},
  _invalidateTransform() {},
  invalidateGrouping() {},
  _invalidateGrouping() {},

  invalidateUpdatedTransform() {},
  _invalidateUpdatedTransform() {},

  fastTranslate() {},
  translate() {},
  scaleFrom() {},
  transform() {},

  replaceColor() {},
  replaceBrush() {},
  setTaggedColors() {},
  setLabeledColors() {},

  clone( otherOwner ) {
    return new OutputProcessor(otherOwner);
  },

  init( ownerFigure) { this.figure = ownerFigure ; },
  invalidateView() {},
  invalidateViewTransform() {}
});



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/detail/processor.js
 **/